// src/beta/components/ProvenanceTimeline.tsx
import { Fragment } from 'react';
import type { V1ProvenanceEntry } from '@/beta/types/dossier';

interface Props {
  entries: V1ProvenanceEntry[];
}

function decadeOf(year: number | null): number | null {
  if (year == null) return null;
  return Math.floor(year / 10) * 10;
}

function yearsLabel(e: V1ProvenanceEntry): string {
  if (e.year_from == null) return '—';
  if (e.is_ongoing || e.year_to == null) return `${e.year_from}—present`;
  if (e.year_from === e.year_to) return `${e.year_from}`;
  return `${e.year_from}—${e.year_to}`;
}

/** § 6.4 (3): Provenance timeline.
 *  Vertical list ordered by year_from ascending. Decade banners ("1970s")
 *  appear whenever the next entry crosses into a new decade. */
export function ProvenanceTimeline({ entries }: Props) {
  if (entries.length === 0) {
    return (
      <p style={{ color: 'var(--vm-stone)', fontSize: 13, fontFamily: 'var(--vm-font-body)' }}>
        No provenance entries on file.
      </p>
    );
  }

  const sorted = [...entries].sort((a, b) => (a.year_from ?? 0) - (b.year_from ?? 0));

  let lastDecade: number | null = null;

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, position: 'relative' }}>
      {/* Vertical rule */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 80,
          top: 0,
          bottom: 0,
          width: 1,
          background: 'var(--vm-border)',
        }}
      />

      {sorted.map((e) => {
        const decade = decadeOf(e.year_from);
        const showDecade = decade != null && decade !== lastDecade;
        if (showDecade) lastDecade = decade;

        return (
          <Fragment key={e.id}>
            {showDecade && (
              <li
                aria-hidden
                style={{
                  fontFamily: 'var(--vm-font-heading)',
                  fontSize: 11,
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  color: 'var(--vm-stone)',
                  padding: 'var(--vm-s-3) 0 var(--vm-s-1)',
                }}
              >
                {decade}s
              </li>
            )}
            <li
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                gap: 'var(--vm-s-3)',
                padding: 'var(--vm-s-2) 0',
                borderBottom: '1px solid var(--vm-border)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--vm-font-heading)',
                  fontSize: 13,
                  color: 'var(--vm-warm-white)',
                }}
              >
                {yearsLabel(e)}
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--vm-font-heading)',
                    fontSize: 11,
                    letterSpacing: '0.3em',
                    textTransform: 'uppercase',
                    color: 'var(--vm-red)',
                  }}
                >
                  {e.role}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--vm-font-heading)',
                    fontSize: 18,
                    color: 'var(--vm-warm-white)',
                    marginTop: 4,
                  }}
                >
                  {e.title}
                </div>
                {e.description && (
                  <div
                    style={{
                      fontFamily: 'var(--vm-font-body)',
                      fontSize: 13,
                      color: 'var(--vm-stone)',
                      marginTop: 4,
                    }}
                  >
                    {e.description}
                  </div>
                )}
                {e.location && (
                  <div
                    style={{
                      fontFamily: 'var(--vm-font-body)',
                      fontSize: 12,
                      color: 'var(--vm-stone)',
                      marginTop: 2,
                    }}
                  >
                    {e.location}
                  </div>
                )}
              </div>
            </li>
          </Fragment>
        );
      })}
    </ul>
  );
}
